import React, { useEffect, useRef, useState } from 'react'
import { FiChevronDown } from 'react-icons/fi'
import AccountStatus from './AccountStatus'
import SmConnectionUninstallModal from './SmConnectionUninstallModal'
import ShippoDisconnectModal from './ShippoDisconnectModal'
import UninstallModal from './UninstallModal'
import {
  resolveShippoDisconnectAccount,
  type ShippoDisconnectAccount,
} from '../shippoDisconnectResolve'
import {
  hasSmIntegrationUninstall,
  isUnlinkModalIntegration,
  unlinkIdentifierFromAccount,
} from '../smUninstallEndpoints'
import { accountDisplayId } from '../accountDisplay'

const backfillBtnClassName =
  'tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-l-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-border-blue-200 hover:tw-bg-blue-50 hover:tw-text-blue-700 tw-transition-colors'

const backfillOnlyBtnClassName =
  'tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-border-blue-200 hover:tw-bg-blue-50 hover:tw-text-blue-700 tw-transition-colors'

const caretBtnClassName =
  'tw-inline-flex tw-items-center tw-justify-center tw-rounded-r-md tw-border tw-border-l-0 tw-border-slate-200 tw-bg-white tw-px-1.5 tw-py-1 tw-text-slate-400 hover:tw-border-blue-200 hover:tw-bg-blue-50 hover:tw-text-blue-700 tw-transition-colors'

const disconnectBtnClassName =
  'tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-border-rose-200 hover:tw-bg-rose-50 hover:tw-text-rose-700 tw-transition-colors'

function normalizeConnEmail(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
}

/** Shared users without team-wide integration permission may still disconnect rows they connected. */
function accountMatchesCurrentUser(account: Record<string, unknown>, currentUserEmail: string): boolean {
  const u = normalizeConnEmail(currentUserEmail)
  if (!u) return false
  return normalizeConnEmail(account.account_email) === u || normalizeConnEmail(account.owner) === u
}

function SplitActionButton({
  onBackfill,
  onDisconnect,
}: {
  onBackfill: () => void
  onDisconnect: () => void
}) {
  const [open, setOpen] = useState(false)
  const caretRef = useRef<HTMLButtonElement>(null)
  const [pos, setPos] = useState({ top: 0, right: 0 })

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (caretRef.current && !caretRef.current.closest('[data-split-btn]')?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleCaretClick = () => {
    if (caretRef.current) {
      const rect = caretRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    }
    setOpen((o) => !o)
  }

  return (
    <div data-split-btn className="tw-relative tw-inline-flex tw-items-stretch">
      <button type="button" className={backfillBtnClassName} onClick={onBackfill}>
        Backfill
      </button>
      <button
        ref={caretRef}
        type="button"
        className={caretBtnClassName}
        onClick={handleCaretClick}
        aria-label="More actions"
      >
        <FiChevronDown className="tw-h-3 tw-w-3" />
      </button>
      {open && (
        <div
          style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 9999 }}
          className="tw-min-w-[110px] tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-py-1 tw-shadow-lg"
        >
          <button
            type="button"
            className="tw-block tw-w-full tw-px-3 tw-py-1.5 tw-text-left tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-bg-rose-50 hover:tw-text-rose-700 tw-transition-colors"
            onClick={() => { setOpen(false); onDisconnect() }}
          >
            Disconnect
          </button>
        </div>
      )}
    </div>
  )
}

export default function ChannelAccountTable({
  accounts,
  isAdmin,
  integration = null,
  ownerId = null,
  retrieveAccounts = undefined,
  ppKeys = undefined,
  /** Disconnect/delete any team connection (owner/admin or My Team permission). Backfill is not gated here. */
  canDisconnectAllTeamIntegrations = false,
  /** When true and canDisconnectAllTeamIntegrations is false, allow disconnect only on rows tied to currentUserEmail. */
  allowDisconnectOwnConnections = false,
  currentUserEmail = '',
  backfillEnabled = false,
  onBackfillForAccount = undefined,
}: {
  accounts: any[]
  isAdmin?: boolean
  integration?: any
  ownerId?: string | number | null
  retrieveAccounts?: (() => void | Promise<void>) | undefined
  ppKeys?: any
  canDisconnectAllTeamIntegrations?: boolean
  allowDisconnectOwnConnections?: boolean
  currentUserEmail?: string
  backfillEnabled?: boolean
  onBackfillForAccount?: (account: any) => void
}) {
  const cellPaddingClassName = 'tw-px-2 tw-py-1'
  const [uninstallConnectionId, setUninstallConnectionId] = useState<string | null>(null)
  const [shippoDisconnectAccount, setShippoDisconnectAccount] = useState<ShippoDisconnectAccount | null>(null)
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false)
  const [unlinkInitialIdentifier, setUnlinkInitialIdentifier] = useState('')

  const channelId = integration?.id

  const buildDisconnectHandler = (account: any) => {
    const mid = account.merchant_id != null ? String(account.merchant_id).trim() : ''
    const shippoResolved =
      channelId === 'shippo'
        ? resolveShippoDisconnectAccount(account as Record<string, unknown>, ppKeys)
        : null
    const showShippoBtn = Boolean(shippoResolved)
    const showSmBtn =
      channelId === 'makeswift'
        ? Boolean(hasSmIntegrationUninstall(channelId))
        : Boolean(hasSmIntegrationUninstall(channelId) && mid)
    const showUnlinkBtn = Boolean(integration && isUnlinkModalIntegration(integration))
    if (!showShippoBtn && !showSmBtn && !showUnlinkBtn) return null
    return () => {
      if (showShippoBtn && shippoResolved) {
        setShippoDisconnectAccount(shippoResolved)
      } else if (showUnlinkBtn && integration?.unlinkIdCol) {
        setUnlinkInitialIdentifier(
          unlinkIdentifierFromAccount(account as Record<string, unknown>, integration.unlinkIdCol)
        )
        setUnlinkModalOpen(true)
      } else if (showSmBtn) {
        if (channelId === 'makeswift') {
          const fromKey =
            account._keyId != null ? String(account._keyId).split('--')[0]?.trim() || '' : ''
          setUninstallConnectionId(fromKey || 'default')
        } else {
          setUninstallConnectionId(mid)
        }
      }
    }
  }

  const rows = accounts.map((account) => ({ account, cardId: accountDisplayId(account) }))

  const rowActionsByIndex = rows.map(({ account }) => {
    const disconnectHandler = buildDisconnectHandler(account)
    const canDisconnect =
      Boolean(disconnectHandler) &&
      (canDisconnectAllTeamIntegrations ||
        (allowDisconnectOwnConnections && accountMatchesCurrentUser(account as Record<string, unknown>, currentUserEmail)))
    const canBackfill = backfillEnabled && Boolean(onBackfillForAccount)
    return { account, disconnectHandler, canDisconnect, canBackfill }
  })

  const hasId = rows.some(({ cardId }) => cardId)
  const hasEmail =
    channelId !== 'makeswift' && rows.some(({ account }) => account.account_email)

  const showActionsColumn = rowActionsByIndex.some((r) => r.canBackfill || r.canDisconnect)

  const columns = [
    ...(hasId
      ? [{ label: channelId === 'makeswift' ? 'PayHelm email' : 'ID', key: 'id' }]
      : []),
    ...(hasEmail ? [{ label: 'Email', key: 'email' }] : []),
    { label: 'Status', key: 'status' },
    ...(showActionsColumn ? [{ label: 'Actions', key: 'actions' }] : []),
  ]

  return (
    <>
      <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-w-full tw-text-sm tw-shadow-sm tw-overflow-hidden">
        <table className="tw-table-fixed tw-border-collapse tw-w-full">
          <thead className="tw-border-b tw-font-medium tw-text-slate-500 tw-bg-slate-50 tw-text-left">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`${cellPaddingClassName}${
                    col.key === 'id'
                      ? ' tw-w-[32%]'
                      : col.key === 'email'
                      ? ' tw-w-[22%]'
                      : col.key === 'status'
                      ? ' tw-w-[12%] tw-whitespace-nowrap'
                      : ' tw-text-center tw-whitespace-nowrap'
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map(({ account, cardId }, i) => (
              <tr
                key={account._keyId || account.merchant_id || i}
                className={i !== 0 ? 'tw-border-t tw-border-slate-200' : ''}
              >
                {columns.map((col) => {
                  let content: React.ReactNode = null
                  if (col.key === 'id') content = cardId
                  else if (col.key === 'email') content = account.account_email
                  else if (col.key === 'status') content = <AccountStatus account={account} />
                  else if (col.key === 'actions') {
                    const ra = rowActionsByIndex[i]
                    const disconnectHandler = ra?.canDisconnect ? ra.disconnectHandler : null
                    const canBackfill = Boolean(ra?.canBackfill)

                    if (canBackfill && disconnectHandler) {
                      content = (
                        <SplitActionButton
                          onBackfill={() => onBackfillForAccount!(account)}
                          onDisconnect={disconnectHandler}
                        />
                      )
                    } else if (canBackfill) {
                      content = (
                        <button
                          type="button"
                          className={backfillOnlyBtnClassName}
                          onClick={() => onBackfillForAccount!(account)}
                        >
                          Backfill
                        </button>
                      )
                    } else if (disconnectHandler) {
                      content = (
                        <button
                          type="button"
                          className={disconnectBtnClassName}
                          onClick={disconnectHandler}
                        >
                          Disconnect
                        </button>
                      )
                    }
                  }
                  const isTruncatable = col.key === 'id' || col.key === 'email'
                  return (
                    <td
                      key={col.key}
                      className={`${cellPaddingClassName}${col.key === 'actions' ? ' tw-text-center tw-whitespace-nowrap' : ''}`}
                    >
                      {isTruncatable ? (
                        <span className="tw-block tw-truncate" title={String(content ?? '')}>
                          {content}
                        </span>
                      ) : (
                        <span className="tw-whitespace-nowrap">{content}</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {integration && hasSmIntegrationUninstall(channelId) && uninstallConnectionId ? (
        <SmConnectionUninstallModal
          visible={Boolean(uninstallConnectionId)}
          closeModal={() => setUninstallConnectionId(null)}
          channelId={integration.id}
          channelTitle={integration.channel || integration.title || integration.id}
          connectionId={uninstallConnectionId}
          ownerId={ownerId}
        />
      ) : null}

      {integration?.id === 'shippo' && shippoDisconnectAccount ? (
        <ShippoDisconnectModal
          visible={Boolean(shippoDisconnectAccount)}
          closeModal={() => setShippoDisconnectAccount(null)}
          account={shippoDisconnectAccount}
          channelTitle={integration.channel || integration.title || 'Shippo'}
          ownerId={ownerId}
          retrieveAccounts={retrieveAccounts ? async () => { await Promise.resolve(retrieveAccounts()) } : undefined}
        />
      ) : null}

      {integration && isUnlinkModalIntegration(integration) ? (
        <UninstallModal
          visible={unlinkModalOpen}
          closeModal={() => {
            setUnlinkModalOpen(false)
            setUnlinkInitialIdentifier('')
            retrieveAccounts?.()
          }}
          integration={integration}
          initialIdentifier={unlinkInitialIdentifier}
        />
      ) : null}
    </>
  )
}
